import { useMemo, useState } from 'react';
import {
  IconAlert,
  IconBox,
  IconCheck,
  IconEdit,
  IconPlus,
  IconPrint,
  IconSearch,
  IconTrash,
  IconTruck,
  IconUpload,
} from '../components/icons';
import { Badge, Card, EmptyState, ErrorMessage, Field, Loading, Modal, ModuleHeader, SearchSelect, StatCard, useAsync, useToast } from '../components/ui';
import { AuthImage } from '../components/AuthImage';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatCurrency, formatDate, formatNumber } from '../lib/format';
import { printList } from '../lib/print';

const TYPES: Record<string, string> = {
  transporter: 'Transporter',
  pkw: 'PKW',
  anhaenger: 'Anhänger',
  maschine: 'Maschine',
};

const FUELS: Record<string, string> = {
  diesel: 'Diesel',
  benzin: 'Benzin',
  elektro: 'Elektro',
  hybrid: 'Hybrid',
  sonstige: 'Sonstige',
};

/**
 * Wie dringend ist ein Termin?
 *
 * Vier Wochen Vorlauf, weil eine Hauptuntersuchung sich nicht am selben Tag
 * einschieben lässt; abgelaufen ist ein eigener Zustand, denn dann darf das
 * Fahrzeug nicht mehr fahren.
 */
function dueState(date: string | null): 'overdue' | 'soon' | 'ok' | null {
  if (!date) return null;
  const days = Math.floor((new Date(date).getTime() - Date.now()) / 86_400_000);
  if (days < 0) return 'overdue';
  if (days <= 28) return 'soon';
  return 'ok';
}

/**
 * Fuhrpark (Kap. 7).
 *
 * Fahrzeuge werden als Kacheln gezeigt, nicht als Tabellenzeilen: Das Bild ist
 * hier ein echtes Erkennungsmerkmal – wer den Wagen sucht, erkennt ihn schneller
 * am Foto als an der Zeile darunter. Das Kennzeichen steht deshalb groß und
 * bleibt der Bezeichner, unter dem alle über das Fahrzeug sprechen.
 */
export function VehiclesPage() {
  const { can } = useAuth();
  const { notify } = useToast();
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [detailId, setDetailId] = useState<number | null>(null);

  const vehicles = useAsync(
    () => api.get('/api/vehicles', { includeInactive: showInactive ? 'true' : '' }),
    [showInactive],
  );
  const users = useAsync(() => api.get('/api/users').catch(() => []), []);
  const warehouses = useAsync(() => api.get('/api/material/warehouses').catch(() => []), []);

  const mayManage = can('fleet.manage');

  const list = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return vehicles.data ?? [];
    return (vehicles.data ?? []).filter((vehicle: any) =>
      `${vehicle.plate} ${vehicle.name ?? ''} ${vehicle.make ?? ''} ${vehicle.model ?? ''} ${vehicle.first_name ?? ''} ${vehicle.last_name ?? ''}`
        .toLowerCase()
        .includes(needle),
    );
  }, [vehicles.data, search]);

  const dueSoon = list.filter((vehicle: any) => ['soon', 'overdue'].includes(dueState(vehicle.next_inspection) ?? ''));

  const remove = async (vehicle: any, permanent: boolean) => {
    const question = permanent
      ? `Fahrzeug ${vehicle.plate} endgültig löschen? Nur möglich, solange das Fahrzeuglager leer ist.`
      : `Fahrzeug ${vehicle.plate} ausmustern? Es bleibt in der Historie erhalten.`;
    if (!window.confirm(question)) return;
    try {
      await api.delete(`/api/vehicles/${vehicle.id}`, permanent ? { permanent: 'true' } : undefined);
      notify(permanent ? 'Fahrzeug gelöscht.' : 'Fahrzeug ausgemustert.', 'success');
      vehicles.reload();
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Aktion nicht möglich.', 'error');
    }
  };

  return (
    <div className="stack">
      <ModuleHeader
        icon={<IconTruck width={22} height={22} />}
        title="Fuhrpark"
        subtitle={
          <>
            Fahrzeuge mit Kennzeichen, Stammdaten und Bild. Jedes Fahrzeug kann ein Fahrzeuglager führen – damit ist
            jederzeit nachvollziehbar, welches Material in welchem Wagen liegt.
          </>
        }
        pill="Ressourcen"
        tone="info"
        actions={
          <>
            <button className="btn" onClick={() => printFleet(list)}>
              <IconPrint width={14} height={14} /> Liste drucken
            </button>
            {mayManage && (
              <button className="btn primary" onClick={() => setEditing({ isNew: true })}>
                <IconPlus width={15} height={15} /> Fahrzeug anlegen
              </button>
            )}
          </>
        }
      />

      {dueSoon.length > 0 && (
        <div className="alert warning">
          <IconAlert width={16} height={16} />
          <span>
            <strong>{dueSoon.length} Fahrzeug(e)</strong> mit fälliger Hauptuntersuchung:{' '}
            {dueSoon.map((vehicle: any) => `${vehicle.plate} (${formatDate(vehicle.next_inspection)})`).join(', ')}
          </span>
        </div>
      )}

      <div className="grid cols-4">
        <StatCard label="Fahrzeuge" value={list.length} tone="primary" icon={<IconTruck width={17} height={17} />} hint="im Bestand" />
        <StatCard
          label="Mit Fahrer"
          value={list.filter((vehicle: any) => vehicle.user_id).length}
          tone="info"
          icon={<IconCheck width={17} height={17} />}
          hint="fest zugeordnet"
        />
        <StatCard
          label="Mit Fahrzeuglager"
          value={list.filter((vehicle: any) => vehicle.warehouse_id).length}
          tone="accent"
          icon={<IconBox width={17} height={17} />}
          hint="Material an Bord"
        />
        <StatCard
          label="HU fällig"
          value={dueSoon.length}
          tone={dueSoon.length > 0 ? 'warning' : 'success'}
          emphasis={dueSoon.length > 0 ? 'attention' : undefined}
          icon={<IconAlert width={17} height={17} />}
          hint="innerhalb von 4 Wochen"
        />
      </div>

      <Card noBody>
        <div className="card-body tight">
          <div className="row">
            <div style={{ position: 'relative', flex: '1 1 260px', maxWidth: 380 }}>
              <input
                type="search"
                placeholder="Kennzeichen, Modell oder Fahrer suchen …"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                style={{ paddingLeft: 34 }}
              />
              <span style={{ position: 'absolute', left: 11, top: 10, color: 'var(--text-subtle)' }}>
                <IconSearch width={15} height={15} />
              </span>
            </div>
            <label className="checkbox-row">
              <input type="checkbox" checked={showInactive} onChange={(event) => setShowInactive(event.target.checked)} />
              Ausgemusterte anzeigen
            </label>
          </div>
        </div>

        {vehicles.loading ? (
          <Loading />
        ) : list.length === 0 ? (
          <EmptyState
            icon={<IconTruck width={20} height={20} />}
            title="Kein Fahrzeug gefunden"
            hint="Fahrzeuge werden über ihr Kennzeichen geführt und lassen sich mit einem Fahrzeuglager verknüpfen."
          />
        ) : (
          <div className="card-body tight">
            <div className="vehicle-grid">
              {list.map((vehicle: any) => (
                <VehicleCard
                  key={vehicle.id}
                  vehicle={vehicle}
                  mayManage={mayManage}
                  onOpen={() => setDetailId(vehicle.id)}
                  onEdit={() => setEditing(vehicle)}
                  onRemove={(permanent) => remove(vehicle, permanent)}
                  onReactivate={async () => {
                    await api.post(`/api/vehicles/${vehicle.id}/reactivate`);
                    notify('Fahrzeug wieder in Betrieb.', 'success');
                    vehicles.reload();
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </Card>

      {editing && (
        <VehicleModal
          vehicle={editing.isNew ? null : editing}
          users={users.data ?? []}
          warehouses={(warehouses.data ?? []).filter((warehouse: any) => warehouse.kind === 'vehicle')}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            vehicles.reload();
            notify('Fahrzeug gespeichert.', 'success');
          }}
        />
      )}

      {detailId && <VehicleDetailModal id={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}

function VehicleCard({
  vehicle,
  mayManage,
  onOpen,
  onEdit,
  onRemove,
  onReactivate,
}: {
  vehicle: any;
  mayManage: boolean;
  onOpen: () => void;
  onEdit: () => void;
  onRemove: (permanent: boolean) => void;
  onReactivate: () => void;
}) {
  const inspection = dueState(vehicle.next_inspection);

  return (
    <div className={`vehicle-card${vehicle.is_active ? '' : ' retired'}`}>
      <button className="vehicle-photo" onClick={onOpen} title="Fahrzeug öffnen">
        {vehicle.has_image ? (
          <AuthImage
            path={`/api/vehicles/${vehicle.id}/image`}
            fallback={
              <span className="vehicle-photo-empty">
                <IconTruck width={34} height={34} />
              </span>
            }
          />
        ) : (
          <span className="vehicle-photo-empty">
            <IconTruck width={34} height={34} />
          </span>
        )}
        <span className="vehicle-plate">{vehicle.plate}</span>
      </button>

      <div className="vehicle-body">
        <div className="row between">
          <span className="row" style={{ gap: 6, alignItems: 'center', minWidth: 0 }}>
            <strong>{vehicle.name || [vehicle.make, vehicle.model].filter(Boolean).join(' ') || 'Ohne Bezeichnung'}</strong>
          </span>
          <Badge variant="info">{TYPES[vehicle.vehicle_type] ?? vehicle.vehicle_type}</Badge>
        </div>
        <span className="small subtle">
          {[vehicle.make, vehicle.model, vehicle.year].filter(Boolean).join(' · ') || '–'}
        </span>

        <div className="vehicle-facts">
          <span>
            <span className="subtle">Fahrer</span>
            {vehicle.first_name ? `${vehicle.first_name} ${vehicle.last_name}` : 'nicht zugeordnet'}
          </span>
          <span>
            <span className="subtle">Lager</span>
            {vehicle.warehouse_name ? `${vehicle.warehouse_name} · ${vehicle.article_count} Artikel` : 'ohne'}
          </span>
          <span>
            <span className="subtle">Nächste HU</span>
            {vehicle.next_inspection ? (
              <Badge variant={inspection === 'overdue' ? 'danger' : inspection === 'soon' ? 'warning' : 'success'}>
                {formatDate(vehicle.next_inspection)}
              </Badge>
            ) : (
              '–'
            )}
          </span>
          <span>
            <span className="subtle">Kilometerstand</span>
            {vehicle.mileage ? `${formatNumber(vehicle.mileage, 2)} km` : '–'}
          </span>
        </div>

        <div className="row" style={{ marginTop: 'auto', paddingTop: 10 }}>
          <button className="btn sm" onClick={onOpen}>
            Details
          </button>
          <span className="spacer" />
          {mayManage &&
            (vehicle.is_active ? (
              <>
                <button className="btn sm ghost" onClick={onEdit} title="Bearbeiten">
                  <IconEdit width={13} height={13} />
                </button>
                <button className="btn sm ghost" onClick={() => onRemove(false)} title="Ausmustern">
                  <IconTrash width={13} height={13} />
                </button>
              </>
            ) : (
              <>
                <button className="btn sm" onClick={onReactivate}>
                  Wieder in Betrieb
                </button>
                <button className="btn sm ghost" onClick={() => onRemove(true)} title="Endgültig löschen">
                  <IconTrash width={13} height={13} />
                </button>
              </>
            ))}
        </div>
      </div>
    </div>
  );
}

/** Fahrzeugdetails samt Bestand im zugehörigen Fahrzeuglager. */
function VehicleDetailModal({ id, onClose }: { id: number; onClose: () => void }) {
  const vehicle = useAsync(() => api.get(`/api/vehicles/${id}`), [id]);
  const data = vehicle.data;

  const value = (data?.stock ?? []).reduce(
    (sum: number, row: any) => sum + row.quantity * row.purchase_price,
    0,
  );

  return (
    <Modal
      title={data ? `${data.plate} · ${data.name || [data.make, data.model].filter(Boolean).join(' ')}` : 'Fahrzeug'}
      size="wide"
      onClose={onClose}
      footer={
        <button className="btn" onClick={onClose}>
          Schließen
        </button>
      }
    >
      {vehicle.loading || !data ? (
        <Loading />
      ) : (
        <div className="stack">
          {data.has_image > 0 && (
            <AuthImage
              path={`/api/vehicles/${id}/image`}
              style={{ width: '100%', maxHeight: 260, objectFit: 'cover', borderRadius: 'var(--radius-md)' }}
            />
          )}

          <div className="detail-grid">
            <Detail label="Kennzeichen" value={data.plate} />
            <Detail label="Art" value={TYPES[data.vehicle_type] ?? data.vehicle_type} />
            <Detail label="Hersteller / Modell" value={[data.make, data.model].filter(Boolean).join(' ') || '–'} />
            <Detail label="Baujahr" value={data.year ?? '–'} />
            <Detail label="Kraftstoff" value={data.fuel ? (FUELS[data.fuel] ?? data.fuel) : '–'} />
            <Detail label="Fahrgestellnummer" value={data.vin ?? '–'} />
            <Detail label="Erstzulassung" value={data.first_registration ? formatDate(data.first_registration) : '–'} />
            <Detail label="Nächste HU" value={data.next_inspection ? formatDate(data.next_inspection) : '–'} />
            <Detail label="Nächster Service" value={data.next_service ? formatDate(data.next_service) : '–'} />
            <Detail label="Kilometerstand" value={data.mileage ? `${formatNumber(data.mileage, 2)} km` : '–'} />
            <Detail label="Versicherung" value={data.insurer ?? '–'} />
            <Detail label="Versicherungsschein" value={data.policy_no ?? '–'} />
            <Detail
              label="Fahrer"
              value={data.first_name ? `${data.first_name} ${data.last_name}` : 'nicht zugeordnet'}
            />
            <Detail label="Fahrzeuglager" value={data.warehouse_name ?? 'ohne'} />
          </div>

          {data.note && <div className="note-box tone-info">{data.note}</div>}

          <div className="row between">
            <span className="section-title">Material an Bord</span>
            <span className="small subtle">
              {(data.stock ?? []).length} Position(en) · {formatCurrency(value)}
            </span>
          </div>

          {!data.warehouse_id ? (
            <span className="small subtle">
              Diesem Fahrzeug ist kein Fahrzeuglager zugeordnet – es führt daher keinen eigenen Bestand.
            </span>
          ) : (data.stock ?? []).length === 0 ? (
            <span className="small subtle">Das Fahrzeuglager ist derzeit leer.</span>
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Artikel</th>
                    <th className="num">Menge</th>
                    <th className="num">Wert</th>
                  </tr>
                </thead>
                <tbody>
                  {data.stock.map((row: any) => (
                    <tr key={row.id}>
                      <td>
                        {row.name}
                        <div className="small subtle mono">{row.article_no}</div>
                      </td>
                      <td className="num">
                        {formatNumber(row.quantity, 2)} <span className="small subtle">{row.unit}</span>
                      </td>
                      <td className="num small">{formatCurrency(row.quantity * row.purchase_price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function Detail({ label, value }: { label: string; value: any }) {
  return (
    <div className="detail-item">
      <span className="detail-label">{label}</span>
      <span className="detail-value">{value}</span>
    </div>
  );
}

function VehicleModal({
  vehicle,
  users,
  warehouses,
  onClose,
  onSaved,
}: {
  vehicle: any | null;
  users: any[];
  warehouses: any[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { notify } = useToast();
  const [form, setForm] = useState({
    plate: vehicle?.plate ?? '',
    name: vehicle?.name ?? '',
    make: vehicle?.make ?? '',
    model: vehicle?.model ?? '',
    vehicleType: vehicle?.vehicle_type ?? 'transporter',
    fuel: vehicle?.fuel ?? '',
    vin: vehicle?.vin ?? '',
    year: vehicle?.year ? String(vehicle.year) : '',
    firstRegistration: vehicle?.first_registration ?? '',
    nextInspection: vehicle?.next_inspection ?? '',
    nextService: vehicle?.next_service ?? '',
    mileage: vehicle?.mileage ? String(vehicle.mileage) : '',
    insurer: vehicle?.insurer ?? '',
    policyNo: vehicle?.policy_no ?? '',
    userId: vehicle?.user_id ? String(vehicle.user_id) : '',
    warehouseId: vehicle?.warehouse_id ? String(vehicle.warehouse_id) : '',
    note: vehicle?.note ?? '',
  });
  const [image, setImage] = useState<{ mime: string; content: string; preview: string } | null>(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const set = (key: keyof typeof form, value: string) => setForm((current) => ({ ...current, [key]: value }));

  const pickImage = async (file: File) => {
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      notify('Bitte ein JPEG, PNG oder WebP wählen.', 'error');
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      notify('Das Bild ist größer als 4 MB.', 'error');
      return;
    }
    const buffer = new Uint8Array(await file.arrayBuffer());
    let binary = '';
    for (let offset = 0; offset < buffer.length; offset += 8192) {
      binary += String.fromCharCode(...buffer.subarray(offset, offset + 8192));
    }
    const content = btoa(binary);
    setImage({ mime: file.type, content, preview: `data:${file.type};base64,${content}` });
    setRemoveImage(false);
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const payload = {
        ...form,
        name: form.name || null,
        make: form.make || null,
        model: form.model || null,
        fuel: form.fuel || null,
        vin: form.vin || null,
        year: form.year ? Number(form.year) : null,
        firstRegistration: form.firstRegistration || null,
        nextInspection: form.nextInspection || null,
        nextService: form.nextService || null,
        mileage: form.mileage ? Number(form.mileage) : null,
        insurer: form.insurer || null,
        policyNo: form.policyNo || null,
        userId: form.userId ? Number(form.userId) : null,
        warehouseId: form.warehouseId ? Number(form.warehouseId) : null,
        note: form.note || null,
      };

      const id = vehicle
        ? (await api.patch(`/api/vehicles/${vehicle.id}`, payload), vehicle.id)
        : (await api.post('/api/vehicles', payload)).id;

      // Das Bild geht getrennt: Es ist um Größenordnungen größer als die
      // Stammdaten und soll ein Speichern nicht scheitern lassen.
      if (image) await api.put(`/api/vehicles/${id}/image`, { mime: image.mime, content: image.content });
      else if (removeImage) await api.delete(`/api/vehicles/${id}/image`);

      onSaved();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  const hasPreview = Boolean(image) || (vehicle?.has_image && !removeImage);
  const placeholder = (
    <span className="vehicle-photo-empty">
      <IconTruck width={30} height={30} />
    </span>
  );

  return (
    <Modal
      title={vehicle ? `Fahrzeug ${vehicle.plate} bearbeiten` : 'Fahrzeug anlegen'}
      size="xwide"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button className="btn primary" onClick={save} disabled={busy || form.plate.trim().length < 2}>
            Speichern
          </button>
        </>
      }
    >
      <div className="stack">
        <ErrorMessage error={error} />

        <div className="row" style={{ alignItems: 'flex-start', gap: 16 }}>
          <div className="vehicle-upload">
            {image ? (
              <img src={image.preview} alt="" />
            ) : vehicle?.has_image && !removeImage ? (
              <AuthImage path={`/api/vehicles/${vehicle.id}/image`} fallback={placeholder} />
            ) : (
              placeholder
            )}
          </div>
          <div className="stack" style={{ gap: 6 }}>
            <label className="btn sm" style={{ cursor: 'pointer' }}>
              <IconUpload width={13} height={13} /> Bild wählen
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                style={{ display: 'none' }}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void pickImage(file);
                }}
              />
            </label>
            {hasPreview && (
              <button
                className="btn sm ghost"
                onClick={() => {
                  setImage(null);
                  setRemoveImage(true);
                }}
              >
                Bild entfernen
              </button>
            )}
            <span className="small subtle">JPEG, PNG oder WebP, höchstens 4 MB.</span>
          </div>
        </div>

        <div className="form-grid">
          <Field label="Kennzeichen" required hint="Amtliches Kennzeichen – es identifiziert das Fahrzeug.">
            <input value={form.plate} onChange={(event) => set('plate', event.target.value)} placeholder="H-HM 1401" autoFocus />
          </Field>
          <Field label="Interner Name">
            <input value={form.name} onChange={(event) => set('name', event.target.value)} placeholder="Transporter Nord" />
          </Field>
          <Field label="Art">
            <select value={form.vehicleType} onChange={(event) => set('vehicleType', event.target.value)}>
              {Object.entries(TYPES).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Hersteller">
            <input value={form.make} onChange={(event) => set('make', event.target.value)} />
          </Field>
          <Field label="Modell">
            <input value={form.model} onChange={(event) => set('model', event.target.value)} />
          </Field>
          <Field label="Baujahr">
            <input type="number" value={form.year} onChange={(event) => set('year', event.target.value)} />
          </Field>
          <Field label="Kraftstoff">
            <select value={form.fuel} onChange={(event) => set('fuel', event.target.value)}>
              <option value="">– ohne Angabe –</option>
              {Object.entries(FUELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Fahrgestellnummer">
            <input value={form.vin} onChange={(event) => set('vin', event.target.value)} />
          </Field>
          <Field label="Kilometerstand">
            <input type="number" value={form.mileage} onChange={(event) => set('mileage', event.target.value)} />
          </Field>
          <Field label="Erstzulassung">
            <input type="date" value={form.firstRegistration} onChange={(event) => set('firstRegistration', event.target.value)} />
          </Field>
          <Field label="Nächste Hauptuntersuchung">
            <input type="date" value={form.nextInspection} onChange={(event) => set('nextInspection', event.target.value)} />
          </Field>
          <Field label="Nächster Service">
            <input type="date" value={form.nextService} onChange={(event) => set('nextService', event.target.value)} />
          </Field>
          <Field label="Versicherung">
            <input value={form.insurer} onChange={(event) => set('insurer', event.target.value)} />
          </Field>
          <Field label="Versicherungsschein-Nr.">
            <input value={form.policyNo} onChange={(event) => set('policyNo', event.target.value)} />
          </Field>
          <Field label="Fahrer">
            <SearchSelect
              value={String(form.userId ?? '')}
              onChange={(wert) => set('userId', wert)}
              options={(users ?? []).map((person: any) => ({ value: String(person.id), label: `${person.first_name} ${person.last_name}` }))}
              placeholder="– nicht zugeordnet –"
            />
          </Field>
          <Field label="Fahrzeuglager" hint="Bestimmt, welcher Bestand dem Fahrzeug zugerechnet wird.">
            <SearchSelect
              value={String(form.warehouseId ?? '')}
              onChange={(wert) => set('warehouseId', wert)}
              options={(warehouses ?? []).map((warehouse: any) => ({ value: String(warehouse.id), label: String(warehouse.name) }))}
              placeholder="– ohne Lager –"
            />
          </Field>
        </div>

        <Field label="Bemerkung">
          <textarea rows={2} value={form.note} onChange={(event) => set('note', event.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

/** Fuhrparkliste zum Aushängen – mit Prüfterminen, denn danach wird gefragt. */
function printFleet(vehicles: any[]) {
  printList({
    title: 'Fuhrpark',
    subtitle: 'Fahrzeuge mit Zuordnung und Prüfterminen',
    orientation: 'landscape',
    meta: [
      { label: 'Fahrzeuge', value: String(vehicles.length) },
      {
        label: 'HU fällig',
        value: String(vehicles.filter((v) => ['soon', 'overdue'].includes(dueState(v.next_inspection) ?? '')).length),
      },
    ],
    columns: [
      { label: 'Kennzeichen', value: (v: any) => v.plate },
      { label: 'Bezeichnung', value: (v: any) => v.name ?? '–' },
      { label: 'Art', value: (v: any) => TYPES[v.vehicle_type] ?? v.vehicle_type },
      { label: 'Hersteller / Modell', value: (v: any) => [v.make, v.model].filter(Boolean).join(' ') || '–' },
      { label: 'Baujahr', value: (v: any) => v.year ?? '–', align: 'right' },
      { label: 'Fahrer', value: (v: any) => (v.first_name ? `${v.first_name} ${v.last_name}` : '–') },
      { label: 'Fahrzeuglager', value: (v: any) => v.warehouse_name ?? '–' },
      {
        label: 'Nächste HU',
        value: (v: any) => (v.next_inspection ? formatDate(v.next_inspection) : '–'),
        emphasis: (v: any) => {
          const state = dueState(v.next_inspection);
          return state === 'overdue' ? 'danger' : state === 'soon' ? 'warning' : null;
        },
      },
      { label: 'km-Stand', value: (v: any) => (v.mileage ? formatNumber(v.mileage, 2) : '–'), align: 'right' },
    ],
    rows: vehicles,
  });
}
